import { createSelector, type PayloadAction } from "@reduxjs/toolkit";
import type {
  IDrofusInterfaceChangeSeverity,
  IDrofusInterfaceChangeType,
  IDrofusInterfaceDiscipline,
} from "@multiconsult-group/multiplacer-core";
import {
  openapi,
  type PostMultiplacerDrofusInterfaceProjectChangeEventsApiArg,
  type PostMultiplacerDrofusInterfaceProjectChangeEventsByRoomApiArg,
} from "../../openapi";
import { createAppSlice } from "../app/createAppSlice";
import type { IRootState } from "../app/store";

export type IDrofusEndringerViewMode = "changes" | "rooms";

export interface IDrofusEndringerSort {
  field:
    | "runStartedAt"
    | "createdAt"
    | "changeType"
    | "severity"
    | "roomFunctionNo"
    | "roomName"
    | "compCode"
    | "articleName"
    | "field";
  direction: "asc" | "desc";
}

export type IDrofusEndringerOccurrenceRef = {
  projectNo: number;
  runId: string;
  occurrenceId: number;
};

export interface IDrofusEndringerState {
  viewMode: IDrofusEndringerViewMode;
  search: string;
  dateFrom: string;
  dateTo: string;
  changeTypes: IDrofusInterfaceChangeType[];
  affectedDisciplines: IDrofusInterfaceDiscipline[];
  severities: IDrofusInterfaceChangeSeverity[];
  limit: number;
  offset: number;
  sort: IDrofusEndringerSort;
  selectedOccurrenceRef: IDrofusEndringerOccurrenceRef | null;
}

export const DROFUS_ENDRINGER_DISCIPLINE_OPTIONS: Array<{
  value: IDrofusInterfaceDiscipline;
  label: string;
  color: string;
}> = [
  { value: "RIE", label: "RIE", color: "#f59f00" },
  { value: "RIKT", label: "RIKT", color: "#1971c2" },
  { value: "RIAut", label: "RIAUT", color: "#9c36b5" },
  { value: "RIV", label: "RIV", color: "#0c8599" },
];

const formatDateInputValue = (date: Date): string => date.toISOString().slice(0, 10);

const getDefaultDrofusEndringerState = (): IDrofusEndringerState => {
  const dateTo = new Date();
  const dateFrom = new Date(dateTo);
  dateFrom.setDate(dateFrom.getDate() - 7);

  return {
    viewMode: "changes",
    search: "",
    dateFrom: formatDateInputValue(dateFrom),
    dateTo: formatDateInputValue(dateTo),
    changeTypes: [],
    affectedDisciplines: [],
    severities: [],
    limit: 100,
    offset: 0,
    sort: {
      field: "runStartedAt",
      direction: "desc",
    },
    selectedOccurrenceRef: null,
  };
};

const initialState = getDefaultDrofusEndringerState();

export const drofusEndringerSlice = createAppSlice({
  name: "drofusEndringer",
  initialState,
  reducers: (create) => ({
    setDrofusEndringerSearch: create.reducer<string>((state, action) => {
      state.search = action.payload;
      state.offset = 0;
    }),
    setDrofusEndringerViewMode: create.reducer<IDrofusEndringerViewMode>((state, action) => {
      state.viewMode = action.payload;
      state.offset = 0;
    }),
    setDrofusEndringerDateFrom: create.reducer<string>((state, action) => {
      state.dateFrom = action.payload;
      state.offset = 0;
    }),
    setDrofusEndringerDateTo: create.reducer<string>((state, action) => {
      state.dateTo = action.payload;
      state.offset = 0;
    }),
    toggleDrofusEndringerChangeType: create.reducer<IDrofusInterfaceChangeType>(
      (state, action) => {
        state.changeTypes = state.changeTypes.includes(action.payload)
          ? state.changeTypes.filter((item) => item !== action.payload)
          : [...state.changeTypes, action.payload];
        state.offset = 0;
      }
    ),
    toggleDrofusEndringerDiscipline: create.reducer<IDrofusInterfaceDiscipline>(
      (state, action) => {
        state.affectedDisciplines = state.affectedDisciplines.includes(action.payload)
          ? state.affectedDisciplines.filter((item) => item !== action.payload)
          : [...state.affectedDisciplines, action.payload];
        state.offset = 0;
      }
    ),
    setDrofusEndringerLimit: create.reducer<number>((state, action) => {
      state.limit = action.payload;
      state.offset = 0;
    }),
    setDrofusEndringerOffset: create.reducer<number>((state, action) => {
      state.offset = action.payload;
    }),
    setDrofusEndringerSort: create.reducer<IDrofusEndringerSort>((state, action) => {
      state.sort = action.payload;
      state.offset = 0;
    }),
    openDrofusEndringerOccurrenceDetails: create.reducer<IDrofusEndringerOccurrenceRef>(
      (state, action) => {
        state.selectedOccurrenceRef = action.payload;
      }
    ),
    closeDrofusEndringerOccurrenceDetails: create.reducer((state) => {
      state.selectedOccurrenceRef = null;
    }),
    resetDrofusEndringerFilters: create.reducer((state) => {
      const selectedOccurrenceRef = state.selectedOccurrenceRef;
      Object.assign(state, getDefaultDrofusEndringerState());
      state.selectedOccurrenceRef = selectedOccurrenceRef;
    }),
  }),
});

export const {
  setDrofusEndringerSearch,
  setDrofusEndringerViewMode,
  setDrofusEndringerDateFrom,
  setDrofusEndringerDateTo,
  toggleDrofusEndringerChangeType,
  toggleDrofusEndringerDiscipline,
  setDrofusEndringerLimit,
  setDrofusEndringerOffset,
  setDrofusEndringerSort,
  openDrofusEndringerOccurrenceDetails,
  closeDrofusEndringerOccurrenceDetails,
  resetDrofusEndringerFilters,
} = drofusEndringerSlice.actions;

export const selectDrofusEndringerState = (state: IRootState) => state.drofusEndringer;

export const selectDrofusEndringerViewMode = createSelector(
  selectDrofusEndringerState,
  (state) => state.viewMode
);

export const selectDrofusEndringerSelectedOccurrenceRef = createSelector(
  selectDrofusEndringerState,
  (state) => state.selectedOccurrenceRef
);

export const selectDrofusEndringerApiArg = createSelector(
  selectDrofusEndringerState,
  (state: IRootState) => state.design.projectNo,
  (state: IRootState) => state.pnsSelection.selectedIds,
  (
    drofusEndringer,
    projectNo,
    selectedPnsIds
  ): PostMultiplacerDrofusInterfaceProjectChangeEventsApiArg | null => {
    if (!Number.isFinite(projectNo) || projectNo <= 0) return null;

    const search = drofusEndringer.search.trim();
    const pns = Array.from(new Set(selectedPnsIds.map((id) => id.trim()).filter(Boolean)));
    return {
      body: {
        projectNo,
        limit: drofusEndringer.limit,
        offset: drofusEndringer.offset,
        ...(search.length > 0 ? { search } : {}),
        ...(pns.length > 0 ? { pns } : {}),
        ...(drofusEndringer.dateFrom ? { dateFrom: drofusEndringer.dateFrom } : {}),
        ...(drofusEndringer.dateTo ? { dateTo: drofusEndringer.dateTo } : {}),
        ...(drofusEndringer.changeTypes.length > 0
          ? { changeTypes: drofusEndringer.changeTypes }
          : {}),
        ...(drofusEndringer.affectedDisciplines.length > 0
          ? { affectedDisciplines: drofusEndringer.affectedDisciplines }
          : {}),
        ...(drofusEndringer.severities.length > 0
          ? { severities: drofusEndringer.severities }
          : {}),
        sort: drofusEndringer.sort,
      },
    };
  }
);

export const selectDrofusEndringerRoomGroupsApiArg = createSelector(
  selectDrofusEndringerState,
  (state: IRootState) => state.design.projectNo,
  (state: IRootState) => state.pnsSelection.selectedIds,
  (
    drofusEndringer,
    projectNo,
    selectedPnsIds
  ): PostMultiplacerDrofusInterfaceProjectChangeEventsByRoomApiArg | null => {
    if (!Number.isFinite(projectNo) || projectNo <= 0) return null;

    const search = drofusEndringer.search.trim();
    const pns = Array.from(new Set(selectedPnsIds.map((id) => id.trim()).filter(Boolean)));
    return {
      body: {
        projectNo,
        ...(search.length > 0 ? { search } : {}),
        ...(pns.length > 0 ? { pns } : {}),
        ...(drofusEndringer.dateFrom ? { dateFrom: drofusEndringer.dateFrom } : {}),
        ...(drofusEndringer.dateTo ? { dateTo: drofusEndringer.dateTo } : {}),
        ...(drofusEndringer.changeTypes.length > 0
          ? { changeTypes: drofusEndringer.changeTypes }
          : {}),
        ...(drofusEndringer.affectedDisciplines.length > 0
          ? { affectedDisciplines: drofusEndringer.affectedDisciplines }
          : {}),
        ...(drofusEndringer.severities.length > 0
          ? { severities: drofusEndringer.severities }
          : {}),
      },
    };
  }
);

export const selectDrofusEndringerQueryResult = createSelector(
  [selectDrofusEndringerApiArg, (state: IRootState) => state],
  (apiArg, state) => {
    if (!apiArg) return undefined;
    return openapi.endpoints.postMultiplacerDrofusInterfaceProjectChangeEvents.select(apiArg)(state);
  }
);

export const selectDrofusEndringerRoomGroupsQueryResult = createSelector(
  [selectDrofusEndringerRoomGroupsApiArg, (state: IRootState) => state],
  (apiArg, state) => {
    if (!apiArg) return undefined;
    return openapi.endpoints.postMultiplacerDrofusInterfaceProjectChangeEventsByRoom.select(apiArg)(
      state
    );
  }
);

export const selectDrofusEndringerRows = createSelector(
  selectDrofusEndringerQueryResult,
  (queryResult) => queryResult?.data?.items ?? []
);

export const selectDrofusEndringerPagination = createSelector(
  selectDrofusEndringerState,
  selectDrofusEndringerQueryResult,
  (state, queryResult) => {
    const total = queryResult?.data?.total ?? 0;
    const limit = queryResult?.data?.limit ?? state.limit;
    const offset = queryResult?.data?.offset ?? state.offset;
    const rowCount = queryResult?.data?.items.length ?? 0;

    return {
      total,
      limit,
      offset,
      rowCount,
      totalPages: Math.max(1, Math.ceil(total / limit)),
      activePage: Math.floor(offset / limit) + 1,
      firstVisibleRow: total === 0 ? 0 : offset + 1,
      lastVisibleRow: Math.min(offset + rowCount, total),
    };
  }
);

export const selectDrofusEndringerRoomGroupsSummary = createSelector(
  selectDrofusEndringerRoomGroupsQueryResult,
  (queryResult) => {
    const total = queryResult?.data?.total ?? 0;
    const rowCount = queryResult?.data?.items.length ?? 0;

    return {
      total,
      rowCount,
      firstVisibleRow: total === 0 ? 0 : 1,
      lastVisibleRow: rowCount,
    };
  }
);
